import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import {
    Badge, Button, Card, CardBody, CardHeader, Col, Collapse,
    FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row
} from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration';
import useFormatMessage from '../../utils/useFormatMessage';

const DAMAGE_REGIONS = [
    'Ön Tampon / Izgara',
    'Arka Tampon / Bagaj',
    'Sol Yan (Kapılar / Çamurluk)',
    'Sağ Yan (Kapılar / Çamurluk)',
    'Tavan',
    'Ön / Arka Cam',
    'Yan Camlar / Aynalar',
    'Lastik / Jant',
    'Mekanik / Alt Takım',
    'İç Döşeme / Konsol'
];

const REFUSAL_REASONS = [
    'Bayi kapalı / mesai dışı',
    'Bayide fiziki yer yok',
    'Hasar nedeniyle bayi reddi',
    'Yanlış bayi / rota hatası',
    'Müşteri / Bayi iptali',
    'İrsaliye / Belge uyuşmazlığı',
    'Diğer'
];

const SoforPortali = () => {
    const { t } = useFormatMessage();
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [plateFilter, setPlateFilter] = useState('');
    const [openCollapses, setOpenCollapses] = useState({});
    const [processingChassis, setProcessingChassis] = useState(null);

    // Delivery (POD) Modal State
    const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
    const [selectedChassis, setSelectedChassis] = useState(null);
    const [selectedGroupageNo, setSelectedGroupageNo] = useState(null);
    const [deliveryImage, setDeliveryImage] = useState(null);
    const [deliveryImageBase64, setDeliveryImageBase64] = useState(null);
    const fileInputRef = useRef(null);

    // Damage Modal State
    const [damageModalOpen, setDamageModalOpen] = useState(false);
    const [damageChassis, setDamageChassis] = useState('');
    const [damageRegion, setDamageRegion] = useState(DAMAGE_REGIONS[0]);
    const [damageDescription, setDamageDescription] = useState('');
    const [damageResponsible, setDamageResponsible] = useState('Taşıyıcı');
    const [damageImage, setDamageImage] = useState(null);
    const damageFileInputRef = useRef(null);

    // Refusal / Undelivered Modal State
    const [refusalModalOpen, setRefusalModalOpen] = useState(false);
    const [refusalChassis, setRefusalChassis] = useState('');
    const [refusalReason, setRefusalReason] = useState(REFUSAL_REASONS[0]);
    const [refusalDescription, setRefusalDescription] = useState('');

    const getUserId = () => {
        try {
            const raw = localStorage.getItem('employee_id');
            if (!raw) return 1;
            return parseInt(Buffer.from(raw, 'base64').toString('ascii'), 10) || 1;
        } catch (e) {
            return 1;
        }
    };

    const getDriverName = () => {
        return localStorage.getItem('fullName') || localStorage.getItem('name') || 'Şoför';
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const body = {
                employee_id: getUserId(),
                ...(plateFilter.trim() && { plate_number: plateFilter.trim() })
            };

            const response = await api.post('/getAllGroupagesForAgacliByPlate', body);
            if (response.data && Array.isArray(response.data)) {
                const formatted = response.data.map(item => ({
                    grupajNo: item.groupage_no || '',
                    plaka: item.plate_number || '',
                    sofor: item.driver_name || '',
                    aracSayisi: item.vehicle_count || 0,
                    kapiCikisSayisi: item.door_exit_count || 0,
                    fromLocation: item.from_location || item.from || '',
                    toLocation: item.to_location || item.to || '',
                    shippingDate: item.shipping_date || null,
                    shippingType: item.shipping_type || '',
                    status: item.status || 'Sevke Hazır',
                    vehicleDetails: (item.vehicle_details || []).map(v => ({
                        chassisNo: v.chassis_no,
                        doorExitDate: v.door_exit_date,
                        deliveryDate: v.delivery_date
                    }))
                }));

                setDataSource(formatted);

                // Auto open collapses by default
                const collapses = {};
                formatted.forEach(item => {
                    collapses[item.grupajNo] = true;
                });
                setOpenCollapses(collapses);
            } else {
                setDataSource([]);
            }
        } catch (err) {
            console.error('Veri çekme hatası:', err);
            toast.error(err.response?.data?.error || 'Görevler yüklenirken hata oluştu');
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const toggleCollapse = (grupajNo) => {
        setOpenCollapses(prev => ({
            ...prev,
            [grupajNo]: !prev[grupajNo]
        }));
    };

    // 1. Kapı Çıkış
    const handleDoorExit = async (groupageNo, chassisNo) => {
        try {
            setProcessingChassis(chassisNo);
            const exitDate = moment().format('YYYY-MM-DD HH:mm:ss');
            const body = {
                grupaj_no: groupageNo,
                chassis_no: chassisNo,
                user_id: getUserId().toString(),
                kapi_cikis: exitDate
            };

            const response = await api.post('/processDoorExitOrDelivery', body);
            if (response.data?.success) {
                toast.success('Kapı çıkış işlemi başarılı');
                fetchData();
            } else {
                toast.error(response.data?.error || 'Kapı çıkış işlemi başarısız');
            }
        } catch (error) {
            console.error('Kapı çıkış hatası:', error);
            toast.error(error.response?.data?.error || 'Kapı çıkış yapılamadı');
        } finally {
            setProcessingChassis(null);
        }
    };

    // 2. Teslimat (POD)
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
                return;
            }
            setDeliveryImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setDeliveryImageBase64(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeliverySubmit = async () => {
        try {
            setProcessingChassis(selectedChassis);
            const formattedDate = moment().format('YYYY-MM-DD HH:mm:ss');
            const base64Only = deliveryImageBase64?.includes(',')
                ? deliveryImageBase64.split(',')[1]
                : deliveryImageBase64;

            const body = {
                grupaj_no: selectedGroupageNo,
                chassis_no: selectedChassis,
                user_id: getUserId().toString(),
                teslim_tarihi: formattedDate,
                ...(base64Only && { image: base64Only })
            };

            const response = await api.post('/processDoorExitOrDelivery', body);
            if (response.data?.success) {
                toast.success('Araç başarıyla teslim edildi (POD kaydedildi)');
                setDeliveryModalOpen(false);
                setSelectedChassis(null);
                setSelectedGroupageNo(null);
                setDeliveryImage(null);
                setDeliveryImageBase64(null);
                fetchData();
            } else {
                toast.error(response.data?.error || 'Teslim işlemi başarısız');
            }
        } catch (error) {
            console.error('Teslim hatası:', error);
            toast.error(error.response?.data?.error || 'Teslim işlemi sırasında hata oluştu');
        } finally {
            setProcessingChassis(null);
        }
    };

    // 3. Hasar Bildirimi (Row 87)
    const openDamageModal = (chassisNo) => {
        setDamageChassis(chassisNo);
        setDamageRegion(DAMAGE_REGIONS[0]);
        setDamageDescription('');
        setDamageResponsible('Taşıyıcı');
        setDamageImage(null);
        if (damageFileInputRef.current) damageFileInputRef.current.value = '';
        setDamageModalOpen(true);
    };

    const handleDamageSubmit = async () => {
        if (!damageDescription.trim()) {
            toast.warning('Lütfen hasar açıklaması giriniz');
            return;
        }

        try {
            const body = {
                damage_date: moment().format('YYYY-MM-DD HH:mm'),
                chassis_no: damageChassis,
                damage_region: damageRegion,
                reporter_id: getUserId(),
                responsible: damageResponsible,
                description: damageDescription
            };

            const formData = new FormData();
            formData.append('data', JSON.stringify(body));
            if (damageImage) {
                formData.append('image', damageImage);
            }

            await api.post('/damage/add', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('Hasar bildirimi başarıyla kaydedildi');
            setDamageModalOpen(false);
        } catch (error) {
            console.error('Hasar bildirim hatası:', error);
            toast.error(error.response?.data?.error || 'Hasar bildirimi kaydedilemedi');
        }
    };

    // 4. Teslim Edilemedi / İade (Row 85)
    const openRefusalModal = (chassisNo) => {
        setRefusalChassis(chassisNo);
        setRefusalReason(REFUSAL_REASONS[0]);
        setRefusalDescription('');
        setRefusalModalOpen(true);
    };

    const handleRefusalSubmit = async () => {
        if (!refusalDescription.trim()) {
            toast.warning('Lütfen ret açıklaması giriniz');
            return;
        }

        try {
            const combinedReason = `${refusalReason} - ${refusalDescription.trim()}`;
            const response = await api.post('/deleteDeliveryDateByChassis', {
                chassis_no: refusalChassis,
                reason: combinedReason,
                user_id: getUserId()
            });

            if (response.data?.success) {
                toast.success('Teslim edilemedi / iade kaydı oluşturuldu');
                setRefusalModalOpen(false);
                fetchData();
            } else {
                toast.error(response.data?.error || 'İşlem başarısız');
            }
        } catch (error) {
            console.error('İade hatası:', error);
            toast.error(error.response?.data?.error || 'Teslim edilemedi kaydı oluşturulamadı');
        }
    };

    // Toplam İstatistikler
    const totalTrips = dataSource.length;
    const totalVehicles = dataSource.reduce((acc, curr) => acc + curr.aracSayisi, 0);
    const totalDelivered = dataSource.reduce((acc, curr) => {
        const deliveredInTrip = (curr.vehicleDetails || []).filter(v => !!v.deliveryDate).length;
        return acc + deliveredInTrip;
    }, 0);

    return (
        <div className="container-fluid py-3">
            <Breadcrumb parent="Operasyon" current="Şoför Portalı" />

            {/* Şoför Bilgi ve Filtre Banner */}
            <Card className="mb-3 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: '#fff' }}>
                <CardBody className="p-4">
                    <Row className="align-items-center">
                        <Col md="6" sm="12" className="mb-3 mb-md-0">
                            <div className="d-flex align-items-center">
                                <div style={{
                                    width: '54px', height: '54px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginRight: '16px'
                                }}>
                                    <i className="fa fa-truck"></i>
                                </div>
                                <div>
                                    <h4 className="mb-1 text-white font-weight-bold">{getDriverName()}</h4>
                                    <div className="text-white-50" style={{ fontSize: '13px' }}>
                                        <i className="fa fa-id-badge mr-1"></i> Taşıyıcı / Şoför Mobil Görev Paneli
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col md="6" sm="12">
                            <Row className="text-center">
                                <Col xs="4">
                                    <div className="bg-white text-dark rounded p-2 shadow-sm">
                                        <div className="h4 mb-0 text-primary font-weight-bold">{totalTrips}</div>
                                        <small className="text-muted">Atanmış Sefer</small>
                                    </div>
                                </Col>
                                <Col xs="4">
                                    <div className="bg-white text-dark rounded p-2 shadow-sm">
                                        <div className="h4 mb-0 text-warning font-weight-bold">{totalVehicles}</div>
                                        <small className="text-muted">Toplam Araç</small>
                                    </div>
                                </Col>
                                <Col xs="4">
                                    <div className="bg-white text-dark rounded p-2 shadow-sm">
                                        <div className="h4 mb-0 text-success font-weight-bold">{totalDelivered}</div>
                                        <small className="text-muted">Teslim Edilen</small>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* Plaka Filtresi */}
                    <Row className="mt-3 pt-3 border-top border-white-50 align-items-center">
                        <Col md="8" sm="8" xs="12" className="mb-2 mb-md-0">
                            <Input
                                placeholder="Plaka ile filtrele (örn: 34EET501)..."
                                value={plateFilter}
                                onChange={(e) => setPlateFilter(e.target.value.toUpperCase())}
                                onKeyPress={(e) => e.key === 'Enter' && fetchData()}
                                style={{ borderRadius: '20px', padding: '10px 18px', fontSize: '14px' }}
                            />
                        </Col>
                        <Col md="4" sm="4" xs="12" className="d-flex">
                            <Button color="light" className="text-primary font-weight-bold mr-2 w-100" onClick={fetchData} disabled={loading}>
                                <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-search'} mr-1`}></i>
                                {loading ? 'Yükleniyor...' : 'Sorgula'}
                            </Button>
                            {plateFilter && (
                                <Button color="secondary" outline className="text-white border-white" onClick={() => { setPlateFilter(''); setTimeout(fetchData, 100); }}>
                                    <i className="fa fa-times"></i>
                                </Button>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Görev Listesi */}
            {loading && dataSource.length === 0 ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-2 text-muted">Atanmış sefer ve yükler getiriliyor...</p>
                </div>
            ) : dataSource.length === 0 ? (
                <Card className="text-center p-5 shadow-sm border-0">
                    <CardBody>
                        <i className="fa fa-folder-open-o text-muted mb-3" style={{ fontSize: '48px' }}></i>
                        <h5>Aktif Atanmış Sefer Bulunamadı</h5>
                        <p className="text-muted">Şu anda adınıza veya plakanıza atanmış açık bir sevkiyat görevi bulunmamaktadır.</p>
                        <Button color="primary" onClick={fetchData}>
                            <i className="fa fa-refresh mr-1"></i> Yenile
                        </Button>
                    </CardBody>
                </Card>
            ) : (
                <div className="task-list">
                    {dataSource.map((groupage) => {
                        const deliveredCount = (groupage.vehicleDetails || []).filter(v => !!v.deliveryDate).length;
                        const isCompleted = deliveredCount === groupage.aracSayisi && groupage.aracSayisi > 0;
                        const isOpen = openCollapses[groupage.grupajNo];

                        return (
                            <Card key={groupage.grupajNo} className="mb-3 shadow-sm border-0 overflow-hidden">
                                <CardHeader
                                    className="d-flex justify-content-between align-items-center cursor-pointer py-3"
                                    style={{
                                        background: isCompleted ? '#f8f9fa' : '#ffffff',
                                        borderBottom: isOpen ? '1px solid #e9ecef' : 'none'
                                    }}
                                    onClick={() => toggleCollapse(groupage.grupajNo)}
                                >
                                    <div className="d-flex align-items-center flex-wrap">
                                        <Badge color={isCompleted ? 'success' : 'primary'} className="mr-2 px-2 py-1 font-weight-bold" style={{ fontSize: '13px' }}>
                                            {groupage.grupajNo}
                                        </Badge>
                                        <span className="font-weight-bold mr-3" style={{ fontSize: '15px', color: '#2c3e50' }}>
                                            <i className="fa fa-map-marker text-danger mr-1"></i>
                                            {groupage.fromLocation || 'Liman / Depo'} → {groupage.toLocation || 'Bayi / Varış'}
                                        </span>
                                        {groupage.plaka && (
                                            <Badge color="secondary" className="mr-2" style={{ fontSize: '12px' }}>
                                                <i className="fa fa-truck mr-1"></i> {groupage.plaka}
                                            </Badge>
                                        )}
                                        {groupage.sofor && (
                                            <small className="text-muted mr-3">
                                                <i className="fa fa-user-circle mr-1"></i> {groupage.sofor}
                                            </small>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <Badge color={isCompleted ? 'success' : 'info'} pill className="mr-3 px-3 py-1">
                                            {deliveredCount} / {groupage.aracSayisi} Teslim Edildi
                                        </Badge>
                                        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} text-muted`}></i>
                                    </div>
                                </CardHeader>

                                <Collapse isOpen={isOpen}>
                                    <CardBody className="p-3 bg-light">
                                        <div className="vehicles-grid">
                                            {groupage.vehicleDetails && groupage.vehicleDetails.length > 0 ? (
                                                <Row>
                                                    {groupage.vehicleDetails.map((veh, idx) => {
                                                        const hasDoorExit = !!veh.doorExitDate;
                                                        const hasDelivery = !!veh.deliveryDate;
                                                        const isBusy = processingChassis === veh.chassisNo;

                                                        return (
                                                            <Col lg="6" md="12" key={veh.chassisNo} className="mb-3">
                                                                <Card className="h-100 shadow-sm border" style={{
                                                                    backgroundColor: hasDelivery ? '#f4fbf7' : hasDoorExit ? '#fbfcfe' : '#ffffff',
                                                                    borderLeft: hasDelivery ? '5px solid #28a745' : hasDoorExit ? '5px solid #007bff' : '5px solid #ffc107'
                                                                }}>
                                                                    <CardBody className="p-3">
                                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                                            <div>
                                                                                <div className="font-weight-bold font-monospace text-dark" style={{ fontSize: '16px', letterSpacing: '0.5px' }}>
                                                                                    {veh.chassisNo}
                                                                                </div>
                                                                                <small className="text-muted">Araç #{idx + 1}</small>
                                                                            </div>
                                                                            <div>
                                                                                {hasDelivery ? (
                                                                                    <Badge color="success">
                                                                                        <i className="fa fa-check mr-1"></i> Teslim Edildi
                                                                                    </Badge>
                                                                                ) : hasDoorExit ? (
                                                                                    <Badge color="primary">
                                                                                        <i className="fa fa-road mr-1"></i> Yolda / Taşımada
                                                                                    </Badge>
                                                                                ) : (
                                                                                    <Badge color="warning" className="text-dark">
                                                                                        <i className="fa fa-clock-o mr-1"></i> Çıkış Bekliyor
                                                                                    </Badge>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* Durum Zamanları */}
                                                                        <div className="mb-3 py-2 px-3 rounded bg-white border" style={{ fontSize: '12px' }}>
                                                                            {hasDoorExit ? (
                                                                                <div className="text-success mb-1">
                                                                                    <i className="fa fa-check-circle mr-1"></i>
                                                                                    <strong>Kapı Çıkış:</strong> {moment(veh.doorExitDate).format('DD.MM.YYYY HH:mm')}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-muted mb-1">
                                                                                    <i className="fa fa-circle-o mr-1"></i>
                                                                                    <strong>Kapı Çıkış:</strong> Henüz yapılmadı
                                                                                </div>
                                                                            )}
                                                                            {hasDelivery && (
                                                                                <div className="text-info">
                                                                                    <i className="fa fa-check-circle mr-1"></i>
                                                                                    <strong>Teslimat:</strong> {moment(veh.deliveryDate).format('DD.MM.YYYY HH:mm')}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Butonlar */}
                                                                        <div className="d-flex flex-wrap gap-2">
                                                                            {/* 1. Kapı Çıkış Butonu */}
                                                                            {!hasDoorExit && (
                                                                                <Button
                                                                                    color="primary"
                                                                                    size="sm"
                                                                                    className="flex-fill"
                                                                                    disabled={isBusy}
                                                                                    onClick={() => handleDoorExit(groupage.grupajNo, veh.chassisNo)}
                                                                                >
                                                                                    <i className="fa fa-sign-out mr-1"></i> Kapı Çıkış Yap
                                                                                </Button>
                                                                            )}

                                                                            {/* 2. Teslim Et Butonu (POD) */}
                                                                            {hasDoorExit && !hasDelivery && (
                                                                                <Button
                                                                                    color="success"
                                                                                    size="sm"
                                                                                    className="flex-fill"
                                                                                    disabled={isBusy}
                                                                                    onClick={() => {
                                                                                        setSelectedChassis(veh.chassisNo);
                                                                                        setSelectedGroupageNo(groupage.grupajNo);
                                                                                        setDeliveryImage(null);
                                                                                        setDeliveryImageBase64(null);
                                                                                        setDeliveryModalOpen(true);
                                                                                    }}
                                                                                >
                                                                                    <i className="fa fa-check-circle mr-1"></i> Teslim Et (POD)
                                                                                </Button>
                                                                            )}

                                                                            {/* 3. Hasar Bildir Butonu (Row 87) */}
                                                                            <Button
                                                                                color="warning"
                                                                                size="sm"
                                                                                outline
                                                                                className="flex-fill"
                                                                                onClick={() => openDamageModal(veh.chassisNo)}
                                                                            >
                                                                                <i className="fa fa-exclamation-triangle mr-1"></i> Hasar Bildir
                                                                            </Button>

                                                                            {/* 4. Teslim Edilemedi / İade Butonu (Row 85) */}
                                                                            {!hasDelivery && (
                                                                                <Button
                                                                                    color="danger"
                                                                                    size="sm"
                                                                                    outline
                                                                                    className="flex-fill"
                                                                                    onClick={() => openRefusalModal(veh.chassisNo)}
                                                                                >
                                                                                    <i className="fa fa-ban mr-1"></i> İade / Ret
                                                                                </Button>
                                                                            )}
                                                                        </div>
                                                                    </CardBody>
                                                                </Card>
                                                            </Col>
                                                        );
                                                    })}
                                                </Row>
                                            ) : (
                                                <div className="text-center py-3 text-muted">Bu sefer altında araç kaydı bulunamadı.</div>
                                            )}
                                        </div>
                                    </CardBody>
                                </Collapse>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* MODAL 1: TESLİMAT (POD) MODAL */}
            <Modal isOpen={deliveryModalOpen} toggle={() => setDeliveryModalOpen(!deliveryModalOpen)} size="md">
                <ModalHeader toggle={() => setDeliveryModalOpen(!deliveryModalOpen)} className="bg-success text-white">
                    <i className="fa fa-check-square-o mr-2"></i> Araç Teslimat Kaydı (POD)
                </ModalHeader>
                <ModalBody className="p-4">
                    <div className="mb-3 p-3 bg-light rounded">
                        <div><strong>Şase No:</strong> <span className="font-monospace text-primary">{selectedChassis}</span></div>
                        <div><strong>Sefer No:</strong> {selectedGroupageNo}</div>
                        <div><strong>Teslimat Zamanı:</strong> {moment().format('DD.MM.YYYY HH:mm')}</div>
                    </div>

                    <FormGroup>
                        <Label className="font-weight-bold">Teslimat Fişi / Bayi İmzası / Kaşe Fotoğrafı:</Label>
                        <Input
                            type="file"
                            accept="image/*"
                            innerRef={fileInputRef}
                            onChange={handleImageChange}
                        />
                        <small className="text-muted d-block mt-1">İsteğe bağlı: Bayi teslim tutanağı veya kaşe görseli ekleyebilirsiniz.</small>
                    </FormGroup>

                    {deliveryImageBase64 && (
                        <div className="text-center mt-3">
                            <img src={deliveryImageBase64} alt="Teslimat Kanıtı" style={{ maxHeight: '180px', maxWidth: '100%', borderRadius: '6px' }} />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setDeliveryModalOpen(false)}>Vazgeç</Button>
                    <Button color="success" onClick={handleDeliverySubmit} disabled={processingChassis !== null}>
                        <i className="fa fa-check mr-1"></i> Teslimatı Tamamla
                    </Button>
                </ModalFooter>
            </Modal>

            {/* MODAL 2: HASAR BİLDİRİMİ MODAL (Row 87) */}
            <Modal isOpen={damageModalOpen} toggle={() => setDamageModalOpen(!damageModalOpen)} size="md">
                <ModalHeader toggle={() => setDamageModalOpen(!damageModalOpen)} className="bg-warning text-dark font-weight-bold">
                    <i className="fa fa-exclamation-triangle mr-2"></i> Şoför Hasar Bildirimi
                </ModalHeader>
                <ModalBody className="p-4">
                    <div className="mb-3 p-2 bg-light rounded">
                        <strong>Şase:</strong> <span className="font-monospace text-danger font-weight-bold">{damageChassis}</span>
                    </div>

                    <FormGroup>
                        <Label className="font-weight-bold">Hasar Bölgesi:</Label>
                        <Input
                            type="select"
                            value={damageRegion}
                            onChange={(e) => setDamageRegion(e.target.value)}
                        >
                            {DAMAGE_REGIONS.map(reg => (
                                <option key={reg} value={reg}>{reg}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label className="font-weight-bold">Hasar Açıklaması:</Label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="Hasarın detayını ve durumunu yazınız..."
                            value={damageDescription}
                            onChange={(e) => setDamageDescription(e.target.value)}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className="font-weight-bold">Hasar Fotoğrafı:</Label>
                        <Input
                            type="file"
                            accept="image/*"
                            innerRef={damageFileInputRef}
                            onChange={(e) => setDamageImage(e.target.files[0])}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setDamageModalOpen(false)}>İptal</Button>
                    <Button color="warning" onClick={handleDamageSubmit}>
                        <i className="fa fa-paper-plane mr-1"></i> Hasarı Kaydet
                    </Button>
                </ModalFooter>
            </Modal>

            {/* MODAL 3: TESLİM EDİLEMEDİ / İADE MODAL (Row 85) */}
            <Modal isOpen={refusalModalOpen} toggle={() => setRefusalModalOpen(!refusalModalOpen)} size="md">
                <ModalHeader toggle={() => setRefusalModalOpen(!refusalModalOpen)} className="bg-danger text-white">
                    <i className="fa fa-ban mr-2"></i> Teslim Edilemedi / İade Bildirimi
                </ModalHeader>
                <ModalBody className="p-4">
                    <div className="mb-3 p-2 bg-light rounded">
                        <strong>Şase:</strong> <span className="font-monospace font-weight-bold text-danger">{refusalChassis}</span>
                    </div>

                    <FormGroup>
                        <Label className="font-weight-bold">Teslim Edilememe Nedeni:</Label>
                        <Input
                            type="select"
                            value={refusalReason}
                            onChange={(e) => setRefusalReason(e.target.value)}
                        >
                            {REFUSAL_REASONS.map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label className="font-weight-bold">Detaylı Açıklama:</Label>
                        <Input
                            type="textarea"
                            rows="3"
                            placeholder="Bayi veya teslimat noktası ret gerekçesini yazınız..."
                            value={refusalDescription}
                            onChange={(e) => setRefusalDescription(e.target.value)}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setRefusalModalOpen(false)}>Vazgeç</Button>
                    <Button color="danger" onClick={handleRefusalSubmit}>
                        <i className="fa fa-check mr-1"></i> Ret / İade Kaydı Oluştur
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default SoforPortali;
